import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertCircle, Plus, RefreshCw, Search, Tags, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { faqsApi } from '@/lib/api';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';
import { programsV2Api } from '@/lib/api/v2/programs';
import type { Faq, FaqScope } from '@/types/faq';

const Q_MAX = 500;
const ANSWER_MAX = 10000;
const TAG_MAX = 50;
const SYN_MAX = 120;

type EditorState = {
  open: boolean;
  mode: 'create' | 'edit';
  faqId?: string;
  question: string;
  answer: string;
  tagsText: string;
  synonymsText: string;
  scope: FaqScope;
  programIds: string[];
  isActive: boolean;
};

function formatApiError(e: unknown): string {
  if (!(e instanceof Error)) return 'Request failed';
  const body = (e as { responseBody?: { details?: { fieldErrors?: Record<string, string | string[]> } } }).responseBody;
  const fe = body?.details?.fieldErrors;
  if (fe && typeof fe === 'object') {
    const parts = Object.entries(fe).flatMap(([k, v]) =>
      Array.isArray(v) ? v.map((x) => `${k}: ${x}`) : [`${k}: ${v}`]
    );
    if (parts.length) return parts.join(' · ');
  }
  return e.message;
}

function parseTags(tagsText: string): string[] {
  return tagsText
    .split(',')
    .map((t) => t.trim().slice(0, TAG_MAX))
    .filter(Boolean)
    .slice(0, 25);
}

function parseSynonyms(text: string): string[] {
  return text
    .split(/\n/)
    .map((s) => s.trim().slice(0, SYN_MAX))
    .filter(Boolean)
    .slice(0, 40);
}

const OBJECT_ID_HEX_RE = /^[a-f0-9]{24}$/i;

export default function FaqsPage() {
  const navigate = useNavigate();
  const { faqId: routeFaqId } = useParams<{ faqId?: string }>();

  const [items, setItems] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvingDeepLink, setResolvingDeepLink] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'true' | 'false'>('all');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  const [programs, setPrograms] = useState<Array<{ _id: string; title: string }>>([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programPickerSearch, setProgramPickerSearch] = useState('');

  const [deactivateTarget, setDeactivateTarget] = useState<Faq | null>(null);

  const [editor, setEditor] = useState<EditorState>({
    open: false,
    mode: 'create',
    question: '',
    answer: '',
    tagsText: '',
    synonymsText: '',
    scope: 'global',
    programIds: [],
    isActive: true,
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setProgramsLoading(true);
        const res = await programsV2Api.fetchAllPrograms({ page: 1, limit: 500, status: 'all' });
        if (cancelled) return;
        if (res?.success && Array.isArray(res.data?.programs)) {
          setPrograms(
            res.data.programs.map((p) => ({
              _id: p._id,
              title: p.title || p.programId || p._id,
            }))
          );
        }
      } catch {
        if (!cancelled) toast.error('Could not load programs for the FAQ editor');
      } finally {
        if (!cancelled) setProgramsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const programTitleById = useMemo(() => {
    const m = new Map<string, string>();
    programs.forEach((p) => m.set(p._id, p.title));
    return m;
  }, [programs]);

  const filteredProgramsForPicker = useMemo(() => {
    const q = programPickerSearch.trim().toLowerCase();
    if (!q) return programs;
    return programs.filter((p) => p.title.toLowerCase().includes(q) || p._id.toLowerCase().includes(q));
  }, [programs, programPickerSearch]);

  const openCreate = () => {
    if (routeFaqId) navigate('/faqs', { replace: true });
    setProgramPickerSearch('');
    setEditor({
      open: true,
      mode: 'create',
      question: '',
      answer: '',
      tagsText: '',
      synonymsText: '',
      scope: 'global',
      programIds: [],
      isActive: true,
    });
  };

  const openEdit = (faq: Faq) => {
    setProgramPickerSearch('');
    const scope: FaqScope = faq.scope === 'program_specific' ? 'program_specific' : 'global';
    setEditor({
      open: true,
      mode: 'edit',
      faqId: faq._id,
      question: faq.question,
      answer: faq.answer,
      tagsText: (faq.tags || []).join(', '),
      synonymsText: (faq.synonyms || []).join('\n'),
      scope,
      programIds: scope === 'program_specific' ? [...(faq.programIds || [])] : [],
      isActive: faq.isActive,
    });
  };

  const closeEditor = () => {
    setEditor((e) => ({ ...e, open: false }));
    if (routeFaqId) navigate('/faqs', { replace: true });
  };

  /** No GET /api/admin/faqs/:id — scan paginated list, then open the editor (deep links). */
  useEffect(() => {
    const id = routeFaqId?.trim();
    if (!id) return;

    if (!OBJECT_ID_HEX_RE.test(id)) {
      toast.error('Invalid FAQ id');
      navigate('/faqs', { replace: true });
      return;
    }

    let cancelled = false;
    setResolvingDeepLink(true);
    (async () => {
      try {
        const raw = await faqsApi.findByIdViaList(id);
        if (cancelled) return;
        if (!raw || typeof raw !== 'object' || !('_id' in raw)) {
          toast.error('FAQ not found');
          navigate('/faqs', { replace: true });
          return;
        }
        const faq = raw as Faq;
        setProgramPickerSearch('');
        const scope: FaqScope = faq.scope === 'program_specific' ? 'program_specific' : 'global';
        setEditor({
          open: true,
          mode: 'edit',
          faqId: faq._id,
          question: faq.question,
          answer: faq.answer,
          tagsText: (faq.tags || []).join(', '),
          synonymsText: (faq.synonyms || []).join('\n'),
          scope,
          programIds: scope === 'program_specific' ? [...(faq.programIds || [])] : [],
          isActive: faq.isActive,
        });
      } catch (e) {
        if (!cancelled) {
          toast.error(formatApiError(e));
          navigate('/faqs', { replace: true });
        }
      } finally {
        if (!cancelled) setResolvingDeepLink(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeFaqId, navigate]);

  const load = useCallback(
    async (showRefresh = false) => {
      try {
        setError(null);
        if (showRefresh) setRefreshing(true);
        else setLoading(true);

        const res = await faqsApi.list({
          page,
          limit,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(isActiveFilter !== 'all' ? { isActive: isActiveFilter === 'true' } : {}),
        });

        if (res.statusCode !== 200) throw new Error((res as { message?: string }).message || 'Failed to load FAQs');
        setItems((res.data.faqs || []) as Faq[]);
        setTotal(res.data.pagination?.total ?? 0);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load FAQs');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, limit, debouncedSearch, isActiveFilter]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const buildPayload = () => {
    const question = editor.question.trim();
    const answer = editor.answer.trim();
    const tags = parseTags(editor.tagsText);
    const synonyms = parseSynonyms(editor.synonymsText);
    const scope = editor.scope;
    const programIds = scope === 'global' ? [] : editor.programIds;
    return { question, answer, tags, synonyms, scope, programIds, isActive: editor.isActive };
  };

  const handleSave = async () => {
    const { question, answer, tags, synonyms, scope, programIds, isActive } = buildPayload();

    if (!question || !answer) {
      toast.error('Question and answer are required');
      return;
    }
    if (question.length > Q_MAX) {
      toast.error(`Question must be at most ${Q_MAX} characters`);
      return;
    }
    if (answer.length > ANSWER_MAX) {
      toast.error(`Answer must be at most ${ANSWER_MAX} characters`);
      return;
    }
    if (scope === 'program_specific' && programIds.length === 0) {
      toast.error('Choose at least one program for a program-specific FAQ');
      return;
    }

    const payload = {
      question,
      answer,
      tags,
      synonyms,
      scope,
      programIds,
      isActive,
    };

    try {
      if (editor.mode === 'create') {
        const res = await faqsApi.create(payload);
        if (res.statusCode !== 201) throw new Error((res as { message?: string }).message || 'Create failed');
        toast.success('FAQ created');
        const created = res.data?.faq as Faq | undefined;
        if (created?._id) {
          setItems((prev) => {
            if (prev.some((x) => x._id === created._id)) return prev;
            return [created, ...prev];
          });
          setTotal((t) => t + 1);
        }
      } else if (editor.faqId) {
        const res = await faqsApi.update(editor.faqId, payload);
        if (res.statusCode !== 200) throw new Error((res as { message?: string }).message || 'Update failed');
        toast.success('FAQ updated');
      }
      closeEditor();
      void load(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmDeactivate = async () => {
    const faq = deactivateTarget;
    if (!faq) return;
    try {
      const res = await faqsApi.deactivate(faq._id);
      if (res.statusCode !== 200) throw new Error((res as { message?: string }).message || 'Deactivate failed');
      toast.success('FAQ deactivated');
      setDeactivateTarget(null);
      void load(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const formatShort = (iso: string) => formatAdminBusinessEt(iso);

  const filteredCountLabel = useMemo(() => {
    if (isActiveFilter === 'all') return 'All';
    return isActiveFilter === 'true' ? 'Active' : 'Inactive';
  }, [isActiveFilter]);

  const toggleProgramId = (id: string) => {
    setEditor((s) => {
      const has = s.programIds.includes(id);
      return {
        ...s,
        programIds: has ? s.programIds.filter((x) => x !== id) : [...s.programIds, id],
      };
    });
  };

  if (error) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Failed to load FAQs</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => void load()} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Try again
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">FAQs</h1>
          <p className="text-muted-foreground">
            Lead chatbot knowledge base: global or program-specific entries with synonyms for better matching.
          </p>
          {resolvingDeepLink && (
            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
              <RefreshCw className="h-3.5 w-3.5 animate-spin shrink-0" />
              Opening FAQ from link… (there is no single-FAQ API; loading pages until it appears in the list.)
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void load(true)} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            New FAQ
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>FAQ list</CardTitle>
          <CardDescription>Search and filter, then edit or deactivate.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search question, answer, tags, synonyms…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Select
              value={isActiveFilter}
              onValueChange={(v) => {
                setIsActiveFilter(v as 'all' | 'true' | 'false');
                setPage(1);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Active" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {filteredCountLabel} • {total} total
                </span>
                <span>
                  Page {page} of {totalPages}
                </span>
              </div>

              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Question</TableHead>
                      <TableHead className="whitespace-nowrap">Scope</TableHead>
                      <TableHead className="hidden lg:table-cell">Programs</TableHead>
                      <TableHead className="hidden md:table-cell">Tags</TableHead>
                      <TableHead>Active</TableHead>
                      <TableHead className="hidden md:table-cell">Updated</TableHead>
                      <TableHead className="w-[180px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-10">
                          No FAQs found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((faq) => {
                        const scope: FaqScope = faq.scope === 'program_specific' ? 'program_specific' : 'global';
                        const pids = faq.programIds || [];
                        return (
                          <TableRow key={faq._id}>
                            <TableCell className="align-top max-w-[280px]">
                              <div className="font-medium">{faq.question}</div>
                              <div className="text-xs text-muted-foreground line-clamp-2 mt-1">{faq.answer}</div>
                              {(faq.synonyms?.length ?? 0) > 0 && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {faq.synonyms!.length} synonym{faq.synonyms!.length === 1 ? '' : 's'}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="align-top whitespace-nowrap">
                              <Badge variant="outline" className="text-xs">
                                {scope === 'global' ? 'Global' : 'Program'}
                              </Badge>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell align-top text-xs text-muted-foreground max-w-[200px]">
                              {scope === 'global' ? (
                                '—'
                              ) : pids.length === 0 ? (
                                '—'
                              ) : (
                                <span className="line-clamp-2" title={pids.map((id) => programTitleById.get(id) || id).join(', ')}>
                                  {pids.slice(0, 2).map((id) => programTitleById.get(id) || id).join(', ')}
                                  {pids.length > 2 ? ` +${pids.length - 2}` : ''}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="hidden md:table-cell align-top">
                              <div className="flex flex-wrap gap-1">
                                {(faq.tags || []).slice(0, 6).map((t) => (
                                  <Badge key={t} variant="outline" className="text-xs">
                                    <Tags className="h-3 w-3 mr-1" />
                                    {t}
                                  </Badge>
                                ))}
                                {(faq.tags || []).length > 6 && (
                                  <Badge variant="outline" className="text-xs">
                                    +{(faq.tags || []).length - 6}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="align-top">
                              <Badge
                                variant="outline"
                                className={faq.isActive ? 'text-green-700' : 'text-muted-foreground'}
                              >
                                {faq.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="hidden md:table-cell align-top text-xs text-muted-foreground">
                              {formatShort(faq.updatedAt)}
                            </TableCell>
                            <TableCell className="align-top">
                              <div className="flex gap-2 justify-end flex-wrap">
                                <Button size="sm" variant="secondary" onClick={() => openEdit(faq)}>
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={!faq.isActive}
                                  onClick={() => setDeactivateTarget(faq)}
                                  className="gap-2"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Deactivate
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                    Prev
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={editor.open} onOpenChange={(v) => !v && closeEditor()}>
        <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-6 sm:max-w-[720px]">
          <DialogHeader className="shrink-0 space-y-2 pr-8">
            <DialogTitle>{editor.mode === 'create' ? 'Create FAQ' : 'Edit FAQ'}</DialogTitle>
            <DialogDescription>
              Used by the lead chatbot for retrieval. Put the direct answer first; add synonyms visitors might type.
          </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-4">
            <div className="space-y-4 pr-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs text-muted-foreground">Question (max {Q_MAX})</Label>
                  <Input
                    value={editor.question}
                    onChange={(e) => setEditor((s) => ({ ...s, question: e.target.value.slice(0, Q_MAX) }))}
                  />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs text-muted-foreground">Answer (max {ANSWER_MAX})</Label>
                  <Textarea
                    value={editor.answer}
                    onChange={(e) => setEditor((s) => ({ ...s, answer: e.target.value.slice(0, ANSWER_MAX) }))}
                    className="min-h-[140px]"
                  />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs text-muted-foreground">Tags (comma separated, max {TAG_MAX} chars each)</Label>
                  <Input
                    value={editor.tagsText}
                    onChange={(e) => setEditor((s) => ({ ...s, tagsText: e.target.value }))}
                    placeholder="tuition, schedule, admissions"
                  />
                  <p className="text-xs text-muted-foreground">
                    Parsed: {parseTags(editor.tagsText).join(', ') || '—'}
                  </p>
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs text-muted-foreground">Synonyms (one per line, max {SYN_MAX} chars each)</Label>
                  <Textarea
                    value={editor.synonymsText}
                    onChange={(e) => setEditor((s) => ({ ...s, synonymsText: e.target.value }))}
                    placeholder={'how much is it\ncost\nprice'}
                    className="min-h-[88px] font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">{parseSynonyms(editor.synonymsText).length} lines will be sent</p>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label className="text-xs text-muted-foreground">Scope</Label>
                  <Select
                    value={editor.scope}
                    onValueChange={(v) =>
                      setEditor((s) => ({
                        ...s,
                        scope: v as FaqScope,
                        programIds: v === 'global' ? [] : s.programIds,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Global (all programs)</SelectItem>
                      <SelectItem value="program_specific">Program-specific</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Program-specific FAQs apply only when the visitor has selected a program in chat.
                  </p>
                </div>

                {editor.scope === 'program_specific' && (
                  <div className="md:col-span-2 flex min-h-0 flex-col space-y-2 rounded-md border p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between shrink-0">
                      <Label className="text-sm font-medium">Programs</Label>
                      {programsLoading ? (
                        <span className="text-xs text-muted-foreground">Loading programs…</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">{editor.programIds.length} selected</span>
                      )}
                    </div>
                    <Input
                      placeholder="Filter programs…"
                      value={programPickerSearch}
                      onChange={(e) => setProgramPickerSearch(e.target.value)}
                      className="h-9 shrink-0"
                    />
                    <div
                      className="max-h-[min(220px,40vh)] min-h-[120px] overflow-y-auto overflow-x-hidden rounded-md border bg-muted/20"
                      role="list"
                    >
                      <div className="p-2 space-y-1">
                        {filteredProgramsForPicker.length === 0 ? (
                          <p className="text-sm text-muted-foreground px-1 py-6 text-center">No programs match.</p>
                        ) : (
                          filteredProgramsForPicker.map((p) => (
                            <label
                              key={p._id}
                              className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 hover:bg-muted/80"
                            >
                              <Checkbox
                                checked={editor.programIds.includes(p._id)}
                                onCheckedChange={() => toggleProgramId(p._id)}
                                className="mt-0.5 shrink-0"
                              />
                              <span className="text-sm leading-snug break-words">{p.title}</span>
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="md:col-span-2 flex items-center justify-between rounded-md border p-3">
                  <div>
                    <p className="text-sm font-medium">Active</p>
                    <p className="text-xs text-muted-foreground">Inactive FAQs are excluded from bot retrieval.</p>
                  </div>
                  <Switch
                    checked={editor.isActive}
                    onCheckedChange={(v) => setEditor((s) => ({ ...s, isActive: Boolean(v) }))}
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t pt-4 mt-0 gap-2 sm:gap-2">
            <Button variant="outline" onClick={closeEditor}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()}>{editor.mode === 'create' ? 'Create' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deactivateTarget)} onOpenChange={(open) => !open && setDeactivateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this FAQ?</AlertDialogTitle>
            <AlertDialogDescription>
              The bot will stop using this entry. You can keep inactive rows for audit; there is no hard delete in this UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void confirmDeactivate()}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
